/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { Component, ComponentRef, OnDestroy, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import {
    GslbWizardService,
    Layer47WizardService,
    WizardSaveService,
    WizardService,
    WizardSubmissionService,
    WizardTemplateService,
    WizardUtilsService
} from '../../../shared/services/components';
import { LogService, ModalService, NavigationService, NotifyService, UtilsService } from '../../../../../core/services';
import { Observable } from 'rxjs';
import { SaveModalComponent } from '../../../shared/modals';
import { TemplateModel, WizardNavigationModel } from '../../../shared/models';
import { WizardStepModel } from '../../../shared/models/wizardStep.model';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-new-service',
    templateUrl: './newService.component.html',
    styleUrls: ['./newService.component.scss']
})

export class NewServiceComponent extends BaseComponent implements OnDestroy {
    /**
     * Reference to new service container.
     */
    @ViewChild('componentContainer', { read: ViewContainerRef, static: true })
    public componentContainer: ViewContainerRef;

    /**
     * Reference to new service container.
     */
    @ViewChild('modalContainer', { read: ViewContainerRef, static: true })
    public modalContainer: ViewContainerRef;

    /**
     * Reference to new service container.
     */
    @ViewChild('saveTemplateContainer', { read: ViewContainerRef, static: true })
    public saveTemplateContainer: ViewContainerRef;

    /**
     * Save modal Component Reference.
     */
    public saveComponentReference: ComponentRef<any>;

    /**
     * External Show progress bar.
     */
    public showProgressBar: boolean = true;

    /**
     * Store the build description for display.
     */
    public description: any;

    /**
     * Stored wizard mode.
     */
    public wizardMode: string;

    /**
     * A boolean to determine if the wizard data is available or not.
     */
    public parentDataAvailable: boolean = false;

    /**
     * Store data for the wizard.
     */
    public navigationState: WizardNavigationModel;

    /**
     * As above but stores the initial load state.
     * @private
     */
    private initialLoad: boolean = true;

    /**
     * Stored the selected template.
     * @private
     */
    private selectedTemplate: TemplateModel;

    /**
     * Store the current template group.
     * @private
     */
    private templateGroup: 'builds' | 'sections';

    /**
     * Modal Component Reference.
     * @private
     */
    private componentReference: ComponentRef<any>;

    constructor(
        private activatedRoute: ActivatedRoute,
        private gslbWizardService: GslbWizardService,
        private layer47WizardService: Layer47WizardService,
        private logService: LogService,
        private modalService: ModalService,
        private navigationService: NavigationService,
        private notifyService: NotifyService,
        private router: Router,
        private utilsService: UtilsService,
        private wizardSaveService: WizardSaveService,
        private wizardService: WizardService,
        private wizardSubmissionService: WizardSubmissionService,
        private wizardTemplateService: WizardTemplateService,
        private wizardUtilsService: WizardUtilsService
    ) {
        super();

        const navigationState: WizardNavigationModel = <WizardNavigationModel>this.router.getCurrentNavigation()?.extras?.state;
        this.setUpComponent(navigationState);
        this.setupSubscribers();
    }

    ngOnDestroy(): void {
        this.resetControllerState();
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    /**
     * Called on submit in the form, submits each service with all it's data.
     */
    public submitService(): void {
        this.wizardSubmissionService.submit(this.navigationState.parentUrl);
    }

    /**
     * Set up Save modal component.
     */
    public openSaveAsTemplateModal(): void {
        this.wizardSaveService.convertControllerStateObjectForExport()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (template: TemplateModel): void => {
                    template.wizardDefaultMode = this.wizardService.wizardMode;
                    template.wizardStartPosition = this.wizardUtilsService.getStartPosition(this.wizardService.wizardSteps);

                    const saveModal: ComponentRef<any> = this.saveTemplateContainer.createComponent(SaveModalComponent);

                    // Set the template in the wizard service.
                    saveModal.instance.contentToSave = template;
                    saveModal.instance.fileNameReference = 'title';
                    saveModal.instance.preselectFileType = 'application/json';
                    saveModal.instance.disabledFileType = true;
                    saveModal.instance.callBack = this.resetClassVariablesAndExitWizard.bind(this);
                    // Assign component reference.
                    this.saveComponentReference = saveModal;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'NewServiceComponent: openSaveAsTemplateModal',
                        'Failed to get object for export', error);
                }
            });
    }

    /**
     * Is this the first visible step? (helper for displaying descriptions ENUMs on first visible step)
     */
    public isFirstVisibleStep(): boolean {
        const wizardSteps: any = this.wizardService.wizardSteps;
        const activeStepIndex: number = this.wizardService.activeStepIndex;
        const activeSubStepIndex: number = this.wizardService.activeSubStepIndex;
        if (!wizardSteps[activeStepIndex]?.subSteps) {
            return false;
        }
        const firstVisibleStepIndex: any = wizardSteps[activeStepIndex].subSteps.findIndex(
            (item: WizardStepModel): boolean => item.isVisible
        );

        return activeSubStepIndex === firstVisibleStepIndex;
    }

    /**
     * Exit the wizard.
     */
    public exitWizard(): void {
        this.resetControllerState();
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(this.navigationState.parentUrl)).then().catch();
    }

    /**
     * Gets a list service ids and names as pairs.
     * @private
     */
    private getServiceIdsAndNames(): any {
        switch (this.selectedTemplate?.type) {
            case 'layer4':
            case 'layer7':
            case 'monitor' :
                return this.layer47WizardService.getItemIdAndName();
        }
    }

    /**
     * Redirect to parent if wizard data is invalid.
     * @param route
     * @private
     */
    private redirectIfDataInvalid(route: ActivatedRoute): void {
        const path = this.utilsService.getObjectValue(route, 'snapshot.url.0.path');
        switch (path) {
            case 'acl':
                this.router.navigateByUrl(this.navigationService.getEnduranceUrl('/cluster-configuration/manage-acls'))
                    .then().catch();
                break;
            case 'service':
                this.router.navigateByUrl(this.navigationService.getEnduranceUrl('/cluster-configuration/manage-services'))
                    .then().catch();
                break;
        }
    }

    /**
     * Setup page subscribers.
     * @private
     */
    private setupSubscribers(): void {
        this.wizardService.parentStepEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (step: any): void => {
                    this.loadComponentByStep(step);
                }
            });

        this.wizardTemplateService.templateChangeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (template: TemplateModel): void => {
                    if (template) {
                        this.selectedTemplate = this.utilsService.duplicateObject(template);
                        this.initialLoad = true;
                    }
                }
            });

        this.wizardService.wizardModeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (mode: any): void => {
                    // The mode has changed and there is a selected template.
                    if (this.wizardMode !== mode && this.selectedTemplate) {
                        if (this.wizardMode === 'partner' && mode !== 'partner'
                            && this.selectedTemplate.wizardModes.includes('Partner')) {
                            this.confirmPartnerModeChange(mode);
                        } else {
                            this.wizardMode = mode;
                        }
                    }
                }
            });

        this.modalService.cancelSubmission
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (closed: boolean): void => {
                    if (closed) {
                        this.wizardService.wizardModeEmitter.next(this.wizardMode);
                    }
                }
            });
    }

    /**
     * Loads and destroys components based on wizard step.
     * @param step
     * @private
     */
    private loadComponentByStep(step: { service: string; index: any; }): void {
        if (this.componentReference) {
            this.componentReference.destroy();
        }

        if (step.service !== 'templateSelection') {
            if (this.initialLoad) {
                switch (this.selectedTemplate?.type) {
                    case 'layer4':
                    case 'layer7':
                    case 'monitor' :
                        this.layer47WizardService.checkForMissingIps(this.modalContainer, this.componentReference);
                        break;
                }

                this.initialLoad = false;
            }

            const component: Type<any> = this.getComponent(step.service);
            if (!component) {
                this.logService.error(
                    'NewServiceComponent: loadComponentByStep',
                    'No component matches ENUM: ' + step.service
                );
            } else {
                // Set the mode before getting the template
                this.wizardMode = this.wizardService.wizardMode;

                const service: ComponentRef<any> = this.componentContainer.createComponent(component);
                // Setup the component values
                service.instance.template = this.getTemplate(step);
                service.instance.templateIndex = step.index;
                this.description = this.getDescription(step);

                // Assign component reference.
                this.componentReference = service;
            }
        }
    }

    /**
     * Get component for selected template.
     * @param service
     * @private
     */
    private getComponent(service: string): Type<any> {
        switch (this.selectedTemplate?.type) {
            case 'layer4':
                return this.layer47WizardService.getLayer4Components(service);
            case 'layer7':
            case 'monitor':
                return this.layer47WizardService.getLayer7Components(service);
            case 'gslb':
                return this.gslbWizardService.getGslbComponents(service);
            default:
                this.logService.error(
                    'NewServiceComponent:getComponent', 'Unable to match component to selected template'
                );

                return;
        }
    }

    /**
     * Get the step template.
     * @param step
     * @private
     */
    private getTemplate(step: { service: any; index: any; }): any {
        this.templateGroup = (this.selectedTemplate && this.selectedTemplate.builds) && this.wizardMode === 'partner'
            ? 'builds' : 'sections';

        return this.selectedTemplate[this.templateGroup][step.service][step.index];
    }

    /**
     * Get description from template.
     * @param step
     * @private
     */
    private getDescription(step: { service: string; index: any; }): string {
        return this.selectedTemplate.description ? this.wizardTemplateService.formatTemplateDescription(this.selectedTemplate, step) : '';
    }

    /**
     * Confirm partner template mode change.
     * @param mode
     * @private
     */
    private confirmPartnerModeChange(mode: string): void {
        const confirmationObject = {
            title: 'wizard_leaving_partner_mode',
            message: 'wizard_leaving_partner_mode_text',
            callBack: this.changeMode.bind(this, mode),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Change partner template mode.
     * @param mode
     * @private
     */
    private changeMode(mode: string): Observable<any> {
        return new Observable((observer) => {
            this.updateWizardWithMode();
            this.wizardMode = mode;
            observer.next();
            observer.complete();
        });
    }

    /**
     * If only one available template, set it as the selected template.
     * @param navigationState
     * @private
     */
    private setUpComponent(navigationState: any): void {
        if (!navigationState) {
            this.redirectIfDataInvalid(this.activatedRoute);
        } else {
            const hasRequiredData: boolean = this.getAvailableWizardData(navigationState);
            if (hasRequiredData) {
                this.formattedNavigationState(navigationState);

                // Set selected template last.
                if (navigationState.templates.length === 1) {
                    this.selectedTemplate = navigationState.templates;
                }
            } else {
                this.logService.error('setUpComponent', 'Missing navigationState required properties');
            }

            this.parentDataAvailable = hasRequiredData;
        }
    }

    /**
     * Set the wizardDataAvailable variable.
     * @private
     */
    private getAvailableWizardData({ cancelText, parentUrl, templates }): boolean {
        return (cancelText !== '' && parentUrl !== '' && templates?.length > 0);
    }

    /**
     * Format navigation state.
     * @param navigationState
     * @private
     */
    private formattedNavigationState(navigationState: WizardNavigationModel): any {
        const data = {
            availableTemplates: navigationState['template'],
            duplicate: navigationState['duplicate'],
            options: {
                showBackButton: navigationState['showBackButton'] || true,
                showSaveButton: navigationState['showSaveButton'] || !navigationState['service'],
                showWizardMode: navigationState['showBackButton'] || !navigationState['duplicate'],
                showExtraServicesButton: true,
                services: this.getServiceIdsAndNames()
            }
        };

        this.navigationState = Object.assign(data, navigationState);
    }

    /**
     * Poke our service to inform all of our subcomponents to reset.
     * @private
     */
    private resetClassVariablesAndExitWizard(): void {
        this.exitWizard();
    }

    /**
     * Reset controller state.
     * @private
     */
    private resetControllerState(): void {
        this.logService.debug('Class reset', 'The wizard form has been reset');
        this.wizardService.resetWizardService();
        this.notifyService.clearLocal();
    }

    /**
     * Update the wizard.
     * @private
     */
    private updateWizardWithMode(): void {
        // Remove partner from the dropdown.
        this.wizardService.wizardModeDropdown = this.wizardService.wizardModeDropdown.filter(
            wizardMode => wizardMode.key !== 'partner'
        );

        const selectedTemplate = this.utilsService.duplicateObject(this.selectedTemplate);
        const controllerState = this.wizardService.controllerState;
        this.wizardService.controllerState = this.getNewControllerStateByMergingData(selectedTemplate, controllerState);
        this.wizardService.updateWizardStateFromNewTemplate(selectedTemplate);
    }

    /**
     * Create new controller state by merging data from different sources.
     * @param selectedTemplate
     * @param controllerState
     * @private
     */
    private getNewControllerStateByMergingData(selectedTemplate: TemplateModel, controllerState: any): any {
        const storedUserValues = this.wizardSubmissionService.getUserValuesFromWizardControllerState(selectedTemplate, controllerState);
        const storedConfig = this.utilsService.duplicateObject(storedUserValues);

        const tempIds = this.getTempIdsForAllServices(selectedTemplate);
        const newControllerState: any = {};

        // Loop through the services type e.g. frontends, backends etc.
        for (const serviceType in selectedTemplate.sections) {
            if (selectedTemplate.sections.hasOwnProperty(serviceType)) {
                let serviceIndex: number = 0;
                newControllerState[serviceType] = [];

                // Loop through the services in each service type.
                for (const templateSection of selectedTemplate.sections[serviceType]) {
                    newControllerState[serviceType].push(
                        this.getValuesForService(controllerState, serviceType, templateSection, storedConfig, tempIds, serviceIndex)
                    );

                    serviceIndex++;
                }
            }
        }

        return newControllerState;
    }

    /**
     * Create a group of tempIds up front for each service to reference during patch.
     * @param selectedTemplate
     * @private
     */
    private getTempIdsForAllServices(selectedTemplate: TemplateModel): any {
        const tempIds: object = {};
        for (const serviceType in selectedTemplate.sections) {
            if (selectedTemplate.sections.hasOwnProperty(serviceType)) {
                tempIds[serviceType] = [];
                const sectionsServices = selectedTemplate.sections[serviceType];
                tempIds[serviceType] = Array.from({ length: sectionsServices.length }, () => 'temp-' + Date.now() + Math.random());
            }
        }

        return tempIds;
    }

    /**
     * Get values for each service.
     * @param controllerState
     * @param serviceType
     * @param templateSection
     * @param storedConfig
     * @param tempIds
     * @param serviceIndex
     * @private
     */
    private getValuesForService(
        controllerState: any,
        serviceType: string,
        templateSection: any,
        storedConfig: any,
        tempIds: any,
        serviceIndex: number
    ): any {
        const controllerStateService = controllerState[serviceType][0];
        const completeService = this.updateServiceWithMissingProperties(controllerStateService, templateSection);
        const updatedService: any = {};
        templateSection['id'] = { value: '', hidden: true };
        for (const property in templateSection) {
            if (templateSection.hasOwnProperty(property) && property !== 'title') {
                const ids: string[] = ['id', 'aclIds', 'backendId', 'serviceId', 'redirectLocation'];
                if (ids.includes(property)) {
                    updatedService[property] = this.handleIds(serviceType, property, tempIds, serviceIndex);
                } else {
                    updatedService[property] = this.getUpdatedProperty(
                        templateSection, property, completeService, serviceType, storedConfig
                    );
                }
            }
        }

        return updatedService;
    }

    /**
     * Handle Ids
     * @param serviceType
     * @param property
     * @param tempIds
     * @param serviceIndex
     * @private
     */
    private handleIds(serviceType: string, property: string, tempIds: any, serviceIndex: number): any {
        switch (this.selectedTemplate?.type) {
            case 'layer4':
            case 'layer7':
            case 'monitor':
                return this.layer47WizardService.handleIds(serviceType, property, tempIds, serviceIndex);
            default:
                this.logService.error('NewServiceComponent:getComponent', 'Unable to match component to selected template');

                return;
        }
    }

    /**
     * Checks to see if there are properties in the templateProperties that aren't in the completeService.
     * @param completeService
     * @param templateSection
     * @private
     */
    private updateServiceWithMissingProperties(completeService: any, templateSection: any): any {
        const serviceProperties: string[] = Object.keys(completeService);
        const templateProperties: string[] = Object.keys(templateSection);
        const difference: string[] = templateProperties.filter((item: string) => !serviceProperties.includes(item));
        const mismatches: string[] = difference.filter((item: string): boolean => item !== 'title');

        for (const mismatch of mismatches) {
            if (!completeService[mismatch]) {
                completeService[mismatch] = { value: '', hidden: true };
            }
        }

        return completeService;
    }

    /**
     * Get update property.
     * @param templateSection
     * @param property
     * @param completeService
     * @param serviceType
     * @param storedConfig
     * @private
     */
    private getUpdatedProperty(
        templateSection: any,
        property: string,
        completeService: any,
        serviceType: string,
        storedConfig: any
    ): { value: any, hidden: boolean } {
        const templateValue = templateSection[property]?.value;
        if (templateValue === undefined || templateValue === null) {

            return completeService[property];
        } else {
            const storedProperty = this.searchStoredConfigForProperty(serviceType, storedConfig, property);
            if (storedProperty) {
                return this.getStoredUserValues(templateSection, storedProperty, property);
            } else {
                const value = typeof templateValue === 'string' && templateValue.includes('[placeHolder]')
                    ? templateValue.replace('[placeHolder]', '') : templateValue;

                const lastAttemptOfFindingAValue = value ? value : completeService[property]?.value;

                return {
                    value: lastAttemptOfFindingAValue,
                    hidden: templateSection[property].hidden
                };
            }
        }
    }

    /**
     * Search stored config for property.
     * @param serviceType
     * @param storedConfig
     * @param property
     * @private
     */
    private searchStoredConfigForProperty(serviceType: string, storedConfig: any, property: string): any {
        const valueForType = storedConfig[serviceType]?.find((item: any) => item.field === property);
        if (valueForType?.value) {
            return valueForType.value;
        } else {
            for (const service in storedConfig) {
                if (storedConfig.hasOwnProperty(service)) {
                    const altValue = storedConfig[service].find((item: any): boolean => item.field === property);
                    if (altValue?.value) {
                        return altValue.value;
                    }
                }
            }
        }
    }

    /**
     *
     * @param templateSection
     * @param storedProperty
     * @param property
     * @private
     */
    private getStoredUserValues(templateSection: any, storedProperty: any, property: string): {
        value: any, hidden: boolean
    } {
        const templateSectionValue = templateSection[property].value;
        const value = typeof templateSectionValue === 'string' && templateSectionValue.includes('[placeHolder]')
            ? templateSectionValue.replace('[placeHolder]', storedProperty) : templateSectionValue;

        return {
            value: value,
            hidden: templateSection[property].hidden
        };
    }
}
